import { router } from '@client/components/_core/providers/TanstackRouterProvider';
import { getConfig } from '@client/config/runtime';
import { redactAccessKeysDeep } from '@client/utils/telemetry/access-key-redaction';
import { protectReplayTransport } from '@client/utils/telemetry/recording-privacy';
import * as Sentry from '@sentry/react';
import { filterSentryEvent } from './sentry-event-filters';
import { withLcpAfterTtfbMeasurement } from './sentry-transaction-measurements';

// Define the URL you want to ignore
const ignoredUrls = ['https://r.lrkt-in.com'];

if (
    getConfig('ENV', import.meta.env.VITE_ENV) === 'production' ||
    getConfig('ENV', import.meta.env.VITE_ENV) === 'staging'
) {
    Sentry.init({
        transport: (options) => protectReplayTransport(Sentry.makeFetchTransport(options)),
        dsn: 'https://f9fdd54d90cbb7d44fea170d0f44c6e8@o4508222925242368.ingest.us.sentry.io/4508285831217152',
        ignoreErrors: [
            'Non-Error promise rejection captured with value:',
            'AbortError',
            'Fetch is aborted',
            'Session expired',
        ],
        // Public access-key URLs are bearer credentials (driver status link, invoice view,
        // invite secret). Strip them from every event, transaction and breadcrumb before it
        // leaves the browser — request.url, navigation breadcrumbs, span data, exception text.
        // Filtering runs first so a dropped event costs nothing.
        beforeSend: (event) => {
            const kept = filterSentryEvent(event);
            return kept === null ? null : redactAccessKeysDeep(kept);
        },
        beforeSendTransaction: (event) => redactAccessKeysDeep(withLcpAfterTtfbMeasurement(event)),
        beforeBreadcrumb: (breadcrumb) => redactAccessKeysDeep(breadcrumb),
        integrations: [
            // Exactly one BrowserTracing integration. The SDK keys integrations by name and keeps
            // the LAST one registered, so a plain `browserTracingIntegration({...})` alongside the
            // TanStack one used to be discarded together with these options — the request filter
            // never applied (CUR-1053, task 0.3).
            Sentry.tanstackRouterBrowserTracingIntegration(router, {
                shouldCreateSpanForRequest: (url) => {
                    // Check if the URL starts with any of the ignored URLs
                    return !ignoredUrls.some((ignored) => url.startsWith(ignored));
                },
            }),
            Sentry.httpClientIntegration(),
            Sentry.extraErrorDataIntegration(),
            Sentry.httpContextIntegration(),
            Sentry.moduleMetadataIntegration(),
            Sentry.functionToStringIntegration(),
            Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: true }),
            Sentry.contextLinesIntegration(),
            Sentry.breadcrumbsIntegration({
                console: true,
                fetch: true,
                history: true,
                sentry: true,
                dom: true,
                xhr: true,
            }),
        ],
        tracesSampleRate: 0.25,
        environment: getConfig('ENV', import.meta.env.VITE_ENV),

        // Capture Replay for 10% of all sessions,
        // plus for 100% of sessions with an error
        replaysSessionSampleRate: 0.3,
        replaysOnErrorSampleRate: 1.0,
    });

    // Defer heavy integrations (replay + profiling) to a separate chunk loaded on idle
    const idle = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1));
    idle(() => {
        import('./sentry-deferred').then(({ addDeferredIntegrations }) => {
            addDeferredIntegrations();
        });
    });

    console.log('Sentry initialized');
    console.log('environment:', getConfig('ENV', import.meta.env.VITE_ENV));
    Sentry.setTag('in-maintenance-mode', false);
}
